import React, { useState } from 'react';
import { Users, Loader2 } from 'lucide-react';
import authAxios from '../../../lib/authAxios';

const StudentConsentModal = ({ onAccepted }) => {
  const [isChecked, setIsChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleAgree = async () => {
    if (!isChecked || isLoading) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await authAxios.post('/accept-consent');
      const updatedUser = res.data?.data?.user;
      if (onAccepted) {
        onAccepted(updatedUser);
      }
    } catch (err) {
      console.error('Failed to accept consent:', err);
      setError(err.response?.data?.message || 'Failed to submit acceptance. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#f0f2f5] sm:bg-[#eef2f6] p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-[640px] max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200 border border-gray-100">
        
        {/* Header */}
        <div className="p-6 sm:p-7 pb-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center space-x-2.5 mb-4">
            <div className="w-9 h-9 rounded-xl bg-[#616ff6] flex items-center justify-center text-white shadow-sm">
              <Users size={19} />
            </div>
            <div>
              <p className="text-[11px] text-gray-400 font-medium leading-none mb-0.5">powered by furcare</p>
              <h2 className="text-sm font-bold text-gray-900 leading-none">Vet Collab</h2>
            </div>
          </div>
          <h1 className="text-xl font-bold text-[#1e293b] tracking-tight">Student Consent & Terms</h1>
          <p className="text-xs text-[#64748b] mt-1">
            Before continuing, please read and acknowledge the following.
          </p>
        </div>

        {/* Scrollable Terms Content — using standard browser scrollbar */}
        <div className="p-6 sm:p-7 overflow-y-auto flex-1 space-y-6 text-xs sm:text-[13px] text-[#475569] leading-relaxed">
          
          {/* Section 1 */}
          <div>
            <h3 className="font-bold text-[#1e293b] mb-2 text-[13px] sm:text-sm">
              <span className="text-[#616ff6] font-semibold mr-1">1.</span> Explore Cases
            </h3>
            <p className="mb-2.5">
              Cases available in Explore Cases represent the clinical experience, documentation, opinions, and decision-making of the individual veterinarian who shared the case from their associated clinic.
            </p>
            <p>
              These cases are provided to encourage professional discussion and learning. They do not represent official veterinary guidelines, medical standards, or the views of Furcare. Clinical decisions should always be based on accepted veterinary standards, evidence-based medicine, and professional judgment.
            </p>
          </div>

          {/* Section 2 */}
          <div>
            <h3 className="font-bold text-[#1e293b] mb-2 text-[13px] sm:text-sm">
              <span className="text-[#616ff6] font-semibold mr-1">2.</span> Student Clinical Contributions
            </h3>
            <p className="mb-2.5">
              When commenting on, discussing, or providing clinical input on any case, students are expected to maintain professional conduct and ensure that contributions are respectful, evidence-based, and aligned with accepted veterinary clinical standards.
            </p>
            <p>
              Any inappropriate, misleading, offensive, or non-professional content may result in removal of content or suspension of access to the platform.
            </p>
          </div>

          {/* Section 3 */}
          <div>
            <h3 className="font-bold text-[#1e293b] mb-2 text-[13px] sm:text-sm">
              <span className="text-[#616ff6] font-semibold mr-1">3.</span> Try a Case
            </h3>
            <p className="mb-2.5">
              Try a Case uses real clinical cases shared by collaborating veterinarians to generate interactive learning experiences and quizzes using technology.
            </p>
            <p>
              These exercises are intended solely for educational discussion, critical thinking, and learning. They are not a substitute for formal veterinary education, clinical training, academic curricula, or professional supervision, and should not be interpreted as an official educational standard.
            </p>
          </div>

          {/* Section 4 */}
          <div>
            <h3 className="font-bold text-[#1e293b] mb-2 text-[13px] sm:text-sm">
              <span className="text-[#616ff6] font-semibold mr-1">4.</span> Opportunities
            </h3>
            <p className="mb-2.5">
              Job postings, internships, externships, research opportunities, and other opportunities published on the platform are created and managed solely by the individual clinic, organization, or employer posting them.
            </p>
            <p>
              All screening, interviews, hiring decisions, and communication are the sole responsibility of the respective employer. Furcare does not participate in, influence, or guarantee any hiring outcome.
            </p>
          </div>

          {/* Section 5 */}
          <div>
            <h3 className="font-bold text-[#1e293b] mb-2 text-[13px] sm:text-sm">
              <span className="text-[#616ff6] font-semibold mr-1">5.</span> Privacy of Your Work
            </h3>
            <p className="mb-2">
              Your resume, profile, cases, and other submitted content remain private by default.
            </p>
            <p className="mb-2">They will only be shared when you explicitly choose to:</p>
            <ul className="list-disc pl-4 space-y-1 mb-2.5">
              <li>Share your profile or work with another user</li>
              <li>Apply for an opportunity</li>
              <li>Grant access to specific individuals or organizations</li>
            </ul>
            <p>
              Furcare will not publicly share your private submissions without your authorization.
            </p>
          </div>

          {/* Section 6 - Acknowledgement Box */}
          <div className="bg-[#f8fafc] border border-slate-100 rounded-xl p-4 sm:p-5 space-y-2.5">
            <h3 className="font-bold text-[#1e293b] text-[13px] sm:text-sm">
              <span className="text-[#616ff6] font-semibold mr-1">6.</span> Acknowledgement
            </h3>
            <p className="text-[#334155] font-medium">By selecting "I Agree", you confirm that:</p>
            <ul className="space-y-2 text-[#475569]">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#616ff6] mt-1.5 flex-shrink-0" />
                <span>You have read and understood the statements above.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#616ff6] mt-1.5 flex-shrink-0" />
                <span>You agree to use Furcare Vet Collab professionally and responsibly.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#616ff6] mt-1.5 flex-shrink-0" />
                <span>You understand the educational purpose and limitations of the platform.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#616ff6] mt-1.5 flex-shrink-0" />
                <span>You consent to these terms as a condition of accessing Furcare Vet Collab.</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 sm:p-7 pt-4 border-t border-slate-100 bg-white flex-shrink-0 space-y-3.5">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-600 text-xs rounded-lg text-center font-medium">
              {error}
            </div>
          )}

          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
              disabled={isLoading}
              className="w-4 h-4 text-[#616ff6] border-gray-300 rounded focus:ring-[#616ff6] cursor-pointer disabled:opacity-50 accent-[#616ff6]"
            />
            <span className="text-xs font-semibold text-[#334155]">
              I have read and agree to the Furcare Vet Collab Student Terms above.
            </span>
          </label>

          <button
            type="button"
            onClick={handleAgree}
            disabled={!isChecked || isLoading}
            className={`w-full py-3 px-6 rounded-xl font-semibold text-sm text-white shadow-sm transition-all flex items-center justify-center gap-2 ${
              isChecked && !isLoading
                ? 'bg-[#616ff6] hover:bg-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#616ff6] focus:ring-offset-2'
                : 'bg-[#b4c0fe] cursor-not-allowed'
            }`}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin text-white" />
                <span>Submitting...</span>
              </>
            ) : (
              'I Agree — Enter Vet Collab'
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

export default StudentConsentModal;
